import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../menu/Sidebar";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import RealgemsSidebar from "../../realgems/RealgemsSidebar";
import "./ManualOperacoes.css";

const formatDateTime = (value) => {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (_err) {
    return String(value);
  }
};

const normalizePresentations = (settings = {}) => {
  if (Array.isArray(settings.items)) {
    return settings.items
      .map((item, index) => ({
        id: String(item?.id || `presentation-${index + 1}`),
        title: String(item?.title || `Apresentação ${index + 1}`),
        embedUrl: String(item?.embedUrl || "").trim(),
      }))
      .filter((item) => item.embedUrl);
  }

  const legacyUrl = String(settings.embedUrl || "").trim();
  return legacyUrl
    ? [{ id: "presentation-1", title: "Apresentação", embedUrl: legacyUrl }]
    : [];
};

const Apresentacoes = ({ realgems = false }) => {
  const [menuClose, setMenuClose] = useState(false);
  const [presentations, setPresentations] = useState([]);
  const [activePresentationId, setActivePresentationId] = useState("");
  const [presentationsUpdatedAt, setPresentationsUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);

  const activePresentation = useMemo(
    () =>
      presentations.find((item) => item.id === activePresentationId) || presentations[0] || null,
    [activePresentationId, presentations]
  );

  const toggleMenu = () => {
    setMenuClose((prev) => !prev);
  };

  useEffect(() => {
    let mounted = true;

    const loadPresentations = async () => {
      try {
        setLoading(true);
        const response = await Api.get("/public/presentations", {
          params: { audience: realgems ? "REALGEMS" : "LICENSED" },
        });
        const settings = response?.data?.settings || {};
        const nextPresentations = normalizePresentations(settings);

        if (!mounted) return;
        setPresentations(nextPresentations);
        setActivePresentationId(nextPresentations[0]?.id || "");
        setPresentationsUpdatedAt(String(settings.updatedAt || "").trim());
      } catch (err) {
        console.error("Erro ao carregar apresentações:", err);

        if (!mounted) return;
        setPresentations([]);
        setActivePresentationId("");
        setPresentationsUpdatedAt("");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadPresentations();

    return () => {
      mounted = false;
    };
  }, [realgems]);

  return (
    <div className="app-container">
      {realgems ? (
        <RealgemsSidebar close={menuClose} presentationsActive="active" />
      ) : (
        <Sidebar close={menuClose} presentationsActive="active" />
      )}

      <main className="main-content">
        <Topbar page="Apresentações" onToggleMenu={toggleMenu} />

        <section className="manual-card presentations-card">
          <div className="manual-card-header">
            <div>
              <h1 className="manual-card-title">Apresentações</h1>
              <p className="manual-card-description">
                Selecione uma apresentação abaixo para visualizá-la diretamente no sistema.
              </p>
              {presentationsUpdatedAt ? (
                <p className="manual-card-meta">
                  Atualizado em {formatDateTime(presentationsUpdatedAt)}
                </p>
              ) : null}
            </div>
          </div>

          {loading ? (
            <div className="manual-empty-state">Carregando apresentações...</div>
          ) : activePresentation ? (
            <>
              {presentations.length > 1 ? (
                <div className="presentations-selector" aria-label="Apresentações disponíveis">
                  {presentations.map((presentation, index) => (
                    <button
                      key={presentation.id}
                      type="button"
                      className={`presentation-selector-button ${
                        presentation.id === activePresentation.id ? "is-active" : ""
                      }`}
                      onClick={() => setActivePresentationId(presentation.id)}
                    >
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <strong>{presentation.title}</strong>
                    </button>
                  ))}
                </div>
              ) : null}

              <div className="presentation-active-heading">
                <span>Apresentação selecionada</span>
                <h2>{activePresentation.title}</h2>
              </div>

              <div className="manual-frame-wrapper">
                <iframe
                  className="manual-frame"
                  src={activePresentation.embedUrl}
                  title={activePresentation.title}
                  scrolling="no"
                  frameBorder="0"
                  allowTransparency
                  allowFullScreen
                />
              </div>
            </>
          ) : (
            <div className="manual-empty-state">
              Nenhuma apresentação está disponível para este painel no momento.
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Apresentacoes;
